import { inject } from '@angular/core'
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { PermissionsApi } from '@features/permissions/permissions.api'
import { PermissionGroup, PermissionMap } from '@features/permissions/permission.models'
import { SALES_PARENT_ID, SALES_TOOLTIP } from '@features/marketable/marketable.constants'
import { of } from 'rxjs'
import { catchError, switchMap, tap } from 'rxjs/operators'

export const PermissionsStore = signalStore(
  withState({
    loading: undefined,
    permissions: undefined as PermissionMap | undefined,
    unavailableApplications: [] as number[],
  }),
  withComputed(({ permissions }) => ({
    contentPermissions: () => permissions()?.contentPermissions,
    applicationPermissions: () => permissions()?.applicationPermissions,
    salePermissions: () => permissions()?.salePermissions,
    salePermissionsWithContext: () =>
      permissions()?.salePermissions?.map((p) => ({
        ...p,
        parentId: SALES_PARENT_ID[p.id],
        tooltip: SALES_TOOLTIP[p.id],
      })),
    saleContentPermissions: () => permissions()?.saleContentPermissions,
  })),
  withMethods((store) => {
    const api = inject(PermissionsApi)

    return {
      fetchPermissions: rxMethod<void>((source$) =>
        source$.pipe(
          tap(() => patchState(store, { loading: true })),
          switchMap(() =>
            api.fetchPermissions().pipe(
              tap((permissions) => patchState(store, { permissions, loading: false })),
              catchError((err) => {
                console.error('Unable to fetch permissions', err)
                patchState(store, { loading: false })
                return of(undefined)
              }),
            ),
          ),
        ),
      ),
      setUnavailableApplications(unavailableApplications: number[]): void {
        patchState(store, { unavailableApplications: [...unavailableApplications] })
      },
      saleContentPermissionsByID(id: number) {
        return store.saleContentPermissions()?.find((f: PermissionGroup) => f.id === id)
          ?.permissions
      },
      saleContentPermissionsAssignableByID(id: number) {
        return store
          .saleContentPermissions()
          ?.find((f: PermissionGroup) => f.id === id)
          ?.permissions?.filter((p) => p.isAssignable)
      },
    }
  }),
)
